import { computed } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withComputed,
  withState,
} from '@ngrx/signals';
import type {
  EntityId,
  EntityProps,
  EntityState,
} from '@ngrx/signals/entities';

/**
 * Slice of state that stores the id of the currently selected entity.
 */
export type SelectedEntityState = { selectedEntityId: EntityId | null };

/**
 * Add a selected-entity state and a derived `selectedEntity` selector to a Signal Store.
 *
 * Pair with {@link withCrudEntitiesEvents} which exposes `setSelectedEntity()` and
 * handles `selectedEntityChanged` via `withReducer`.
 *
 * @template Entity The entity model stored in the Signal Store's EntityState.
 * @returns A Signal Store feature that can be composed with other features.
 * @example
 * const feature = withSelectedEntity<MyEntity>();
 */
export function withSelectedEntity<Entity>() {
  return signalStoreFeature(
    { state: type<EntityState<Entity>>(), props: type<EntityProps<Entity>>() },
    withState<SelectedEntityState>({ selectedEntityId: null }),
    withComputed(({ entityMap, selectedEntityId }) => ({
      selectedEntity: computed(() => {
        const selectedId = selectedEntityId();
        return selectedId === null ? null : entityMap()[selectedId];
      }),
    }))
  );
}

/**
 * Helper to clear the selected entity id.
 *
 * @returns Partial state with `selectedEntityId: null`.
 */
export function clearSelectedEntity(): SelectedEntityState {
  return { selectedEntityId: null };
}

/**
 * Helper to set the selected entity id.
 *
 * @param selectedEntityId The id of the entity to select.
 * @returns Partial state with `selectedEntityId` set.
 */
export function setSelectedEntity(
  selectedEntityId: number | string
): SelectedEntityState {
  return { selectedEntityId: selectedEntityId };
}
